/**
 * auth 端点的请求/响应适配（#89098）
 *
 * 这一层只做三件事：拼路径、把上游响应解析成 CloudSession、把上游错误归一成
 * CloudError。业务流程在 index.ts，并发与生命周期在 session-manager.ts。
 *
 * 端点路径全部取自 `@cloudbase/js-sdk` 的 ApiUrls 常量表（v3.8.0 的
 * `consts.ts`），与服务端的放行表逐条对齐。**不要凭印象改这些字符串** ——
 * 服务端按路径精确匹配来决定「换签 / 本地处理 / 透传 / 拒绝」，改错一个字符
 * 就会掉到另一条分支上（例如带了尾斜杠会从「本地续期」掉成「透传给上游」）。
 */

import type { CloudError, CloudErrorKind } from '../../http/errors.js';
import type { CloudSession, CloudUser } from './types.js';

/**
 * auth 端点表。
 *
 * ⚠️ 续期**只能**用 v1 的 `/v1/token`。v2 的 `/v2/token` 在服务端落在「换签」
 * 分支、会被转发给上游，而我们手里的 refresh_token 是我方句柄，上游不认识它，
 * 结果必然是 invalid_grant。
 */
export const AUTH_PATHS = {
    signUp: '/v1/signup',
    signIn: '/v1/signin',
    signInWithProvider: '/v1/signin/with/provider',
    /** 本地续期端点（服务端不转发上游）。 */
    token: '/v1/token',
    /** 小程序微信登录：本地认人后仍需用 custom 票据换取 TCB session。 */
    loginWechat: '/v1/login-wechat',
    /** 网页微信扫码：Relay 回调后换 Genie session。 */
    loginWechatWeb: '/v1/login-wechat-web',
    signOut: '/v1/user/signout',
    userMe: '/v1/user/me',
    /** 发码。 */
    verification: '/v1/verification',
    /** 验码，返回一次性的 verification_token（**不是**会话）。 */
    verificationVerify: '/v1/verification/verify',
    /** 忘记密码最终更新。 */
    resetPassword: '/v1/reset',
    /** 登录后改密的二次校验。 */
    sudo: '/v1/user/sudo',
    /** 登录后更新密码。 */
    userPassword: '/v1/user/password',
} as const;

/** 上游返回的会话形状（换签后的我方字段）。 */
interface RawSession {
    access_token?: unknown;
    refresh_token?: unknown;
    expires_in?: unknown;
    sub?: unknown;
    [key: string]: unknown;
}

/**
 * 把换签后的响应解析成 CloudSession。
 *
 * ⚠️ 上游字段是**保留的**：服务端在原始 JSON 上覆盖那五个字段，其余（如
 * `is_anonymous`）原样留着。所以这里按「至少含这几个键」解析，不能假设
 * 响应里只有这五个键。
 */
export function parseSession(payload: unknown, receivedAt: number): CloudSession {
    const raw = (payload ?? {}) as RawSession;
    const accessToken = typeof raw.access_token === 'string' ? raw.access_token : '';
    if (!accessToken) {
        throw sessionShapeError('response has no access_token');
    }
    const refreshToken = typeof raw.refresh_token === 'string' ? raw.refresh_token : '';
    // expires_in 是**秒**（服务端 int，向零截断）。缺失时给 0，交给
    // SessionManager 的 fail-safe 当作已过期处理，而不是在这里编一个默认值。
    const expiresInSec = typeof raw.expires_in === 'number' ? raw.expires_in : 0;

    return {
        accessToken,
        refreshToken,
        expiresAt: receivedAt + expiresInSec * 1000,
        user: userFromSessionPayload(raw),
    };
}

/**
 * 从换签响应里取用户信息。
 *
 * 换签响应只保证有 `sub`，其它字段取决于上游返了什么 —— 需要完整用户信息时
 * 应当另调 `/v1/user/me`。这里刻意不去发那次请求：登录路径上多一个往返会让
 * 首屏变慢，而多数应用只需要一个 id。
 */
function userFromSessionPayload(raw: RawSession): CloudUser {
    const isAnonymous = raw.is_anonymous === true;
    return {
        id: typeof raw.sub === 'string' ? raw.sub : '',
        isAnonymous,
        raw: raw as Record<string, unknown>,
    };
}

/**
 * 解析 `/v1/user/me` 的响应。
 *
 * 该端点服务端**纯透传**，所以拿到的是上游原始 user 对象。字段名两种都见过
 * （`sub` 与旧的 `uid`），都认。
 */
export function parseUser(payload: unknown): CloudUser {
    const raw = (payload ?? {}) as Record<string, unknown>;
    const id =
        typeof raw.sub === 'string' ? raw.sub : typeof raw.uid === 'string' ? raw.uid : '';
    const metadata = (raw.user_metadata ?? {}) as Record<string, unknown>;
    return {
        id,
        email: pickString(raw.email) ?? pickString(metadata.email),
        phone: pickString(raw.phone_number) ?? pickString(raw.phone),
        // 两处都判：上游在不同端点上用过不同字段表达匿名。
        isAnonymous: raw.is_anonymous === true || raw.scope === 'anonymous',
        raw,
    };
}

function pickString(v: unknown): string | undefined {
    return typeof v === 'string' && v !== '' ? v : undefined;
}

/** 上游错误体（OAuth2 形状，服务端归一后固定这两个字段）。 */
interface RawError {
    error?: unknown;
    error_description?: unknown;
    message?: unknown;
    msg?: unknown;
    code?: unknown;
}

/**
 * 把一次失败的响应归一成 CloudError。
 *
 * 映射依据是服务端的 errorMapping：它把内部错误码收敛成 OAuth2 的枚举值 +
 * 固定 HTTP 状态。这里按 (status, error) 双因素判定，不只看状态码 ——
 * 401 既可能是「key 无效」(invalid_client) 也可能是「会话失效」(invalid_grant)，
 * 二者的处置完全不同：前者是配置错，后者要清凭据重登。
 */
export function normalizeHttpError(status: number, payload: unknown): CloudError {
    const raw = (payload ?? {}) as RawError;
    const code = typeof raw.error === 'string' ? raw.error : undefined;
    const message =
        pickString(raw.error_description) ??
        pickString(raw.message) ??
        pickString(raw.msg) ??
        `HTTP ${status}`;

    return { kind: errorKind(status, code), message, status, code, cause: payload };
}

function errorKind(status: number, code?: string): CloudErrorKind {
    if (code === 'invalid_grant') {
        return 'unauthenticated';
    }
    switch (status) {
        case 400:
            return 'invalid-request';
        case 401:
            // 走到这里说明不是 invalid_grant —— 多半是 invalid_client（key 无效
            // 或已吊销）。归到 invalid-request 会让调用方以为改参数就能好。
            return 'unauthenticated';
        case 403:
            return 'permission-denied';
        case 404:
            return 'not-found';
        case 429:
            return 'rate-limited';
        case 501:
            return 'unimplemented';
        case 503:
            return 'backend-unavailable';
        default:
            return status >= 500 ? 'backend-unavailable' : 'unknown';
    }
}

/**
 * fetch 本身失败（离线、DNS、CORS）时的归一。
 *
 * 这条路径**必须**存在：服务端对 403 与 404 刻意不回 CORS 头，所以浏览器里
 * 这两类错误表现为 fetch 抛 TypeError、**读不到响应体**。只处理 HTTP 状态码
 * 的实现会在这里静默漏掉两类真实失败。
 */
export function normalizeNetworkError(err: unknown): CloudError {
    const message = err instanceof Error ? err.message : String(err);
    return {
        kind: 'network',
        message: `request failed before a response was received: ${message}`,
        status: 0,
        cause: err,
    };
}

function sessionShapeError(reason: string): CloudError & Error {
    const e = new Error(`workbuddy-cloud auth: ${reason}`) as CloudError & Error;
    e.kind = 'unknown';
    e.status = 0;
    return e;
}
